import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import { flow, groupBy, sortBy, entries, map, filter } from 'lodash/fp';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { makeStyles, styled } from '@material-ui/core/styles';
import Popover from 'components/@common/Popover';
import useProductCache from 'store/cache/product-cache';
import { getId, sortProductByGrades, sortProductCurriculumByGrades } from 'utilities/product-utils';
import { OrganizationLicenseUsage, Product, ProductCurriculum } from 'types/schema-generated-types';

export type Props = {
  licenseUsage: OrganizationLicenseUsage[];
};

const ProductsTableHeaderCell = styled(TableCell)(({ theme }) => ({
  borderBottom: 'none'
}));

const ProductsTable = ({ licenseUsage }: Props) => {
  const classes = useStyles();
  const [{ findProductByProductCode }] = useProductCache();
  const { t } = useTranslation(['ProductAndLicenses']);

  const licenseUsageByProduct = flow(
    sortBy<OrganizationLicenseUsage>('product.code'),
    groupBy('product.code'),
    entries,
    map(([productCode, usage]: [string, OrganizationLicenseUsage[]]) => {
      const product = findProductByProductCode(productCode);
      return [product, usage];
    }),
    filter(([product]) => !!product)
  )(licenseUsage) as [Product, OrganizationLicenseUsage[]][];

  const rows = licenseUsageByProduct
    .sort(([a], [b]) => sortProductByGrades(a, b))
    .flatMap(([product, usages]: [Product, OrganizationLicenseUsage[]]) => {
      if (!product || !product.curriculums) return [];

      const items: {
        product: Product;
        productCurriculum: ProductCurriculum;
        usage: OrganizationLicenseUsage;
      }[] = [];

      product.curriculums.forEach(productCurriculum => {
        const usage = usages.find(lu => lu.productCurriculum?.id === productCurriculum.id);
        if (usage) items.push({ product, productCurriculum, usage });
      });

      return items.sort((a, b) =>
        sortProductCurriculumByGrades(a.productCurriculum, b.productCurriculum)
      );
    });

  return (
    <TableContainer className={classes.root}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <ProductsTableHeaderCell>{t('licenseHeaderLabel')}</ProductsTableHeaderCell>
            <ProductsTableHeaderCell align="right" className={classes.rightAlignedHeader}>
              <Popover centerIcon inline description={t('assignedInfo')}>
                {t('assignedHeaderLabel')}
              </Popover>
            </ProductsTableHeaderCell>
            <ProductsTableHeaderCell align="right" className={classes.rightAlignedHeader}>
              <Popover centerIcon inline description={t('expSoonInfo')}>
                {t('expiringHeaderLabel')}
              </Popover>
            </ProductsTableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {rows.map(({ product, productCurriculum, usage: { assignedCount, expiringCount } }) => (
            <TableRow key={getId(product, productCurriculum)}>
              <TableCell>
                {productCurriculum.code}: {productCurriculum.name}
              </TableCell>
              <TableCell align="right">{assignedCount}</TableCell>
              <TableCell
                align="right"
                className={cx(classes.expiringSoonText)}
                style={{ paddingRight: 26 }}
              >
                {expiringCount}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length === 0 && (
        <Typography className={classes.emptyContainer}>{t('noAssignedLicenses')}</Typography>
      )}
    </TableContainer>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: 1
  },
  tableBody: {
    borderRadius: 4,
    boxShadow: '0 0 0 1px #e0e0e0',
    '& > tr:last-child > td': {
      borderBottom: 'none'
    }
  },
  expiringSoonText: {
    color: theme.palette.error.main
  },
  rightAlignedHeader: {
    whiteSpace: 'nowrap',
    paddingRight: '0 !important'
  },
  emptyContainer: {
    textAlign: 'center',
    color: theme.palette.grey[500],
    padding: theme.spacing(3)
  }
}));

export default ProductsTable;
